// client/src/components/marketer/modals/MarketerCPSSettlementModal.js
import React, { useState, useEffect, useCallback } from 'react';
import '../../../styles/marketer/modals/MarketerCPSSettlementModal.css';

const API_URL = process.env.REACT_APP_API_URL;

export default function MarketerCPSSettlementModal({ open, onClose, projectId, projectNumber }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedView, setSelectedView] = useState('total'); // 'total' | '1' | '2' | '3'...

  // 데이터 조회
  const fetchData = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/marketer/cps-settlements/${projectId}/summary`, {
        credentials: 'include'
      });
      const result = await res.json();

      if (result.ok) {
        setData(result);
      } else {
        alert(result.message || '데이터 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('CPS 결산 조회 오류:', error);
      alert('데이터 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open && projectId) {
      fetchData();
      setSelectedView('total'); // 모달 열릴 때마다 총합으로 초기화
    }
  }, [open, projectId, fetchData]);

  if (!open) return null;

  // CPS 미설정
  if (data && !data.hasCPS) {
    return (
      <div className="marketer-cps-settlement-modal mbm-overlay">
        <div className="marketer-cps-settlement-modal__content mbm" onClick={(e) => e.stopPropagation()}>
          <div className="marketer-cps-settlement-modal__header mbm-head">
            <h2 className="mbm-title">CPS 결산 상태</h2>
            <button onClick={onClose} className="marketer-cps-settlement-modal__close-icon mbm-close">✕</button>
          </div>
          <p className="marketer-cps-settlement-modal__no-cps">
            이 프로젝트는 구매당 리워드가 설정되지 않았습니다.
          </p>
          <div className="marketer-cps-settlement-modal__footer mbm-foot">
            <button onClick={onClose} className="mb-btn mb-btn-secondary">닫기</button>
          </div>
        </div>
      </div>
    );
  }

  // 현재 선택된 뷰의 데이터
  const currentData = selectedView === 'total' 
    ? data?.total 
    : data?.rounds?.find(r => r.roundNumber === parseInt(selectedView))?.influencers;

  const currentRound = selectedView !== 'total' 
    ? data?.rounds?.find(r => r.roundNumber === parseInt(selectedView))
    : null;

  // 총합 계산
  const totalCustomers = currentData?.reduce((sum, inf) => sum + (inf.customerCount || 0), 0) || 0;
  const totalReward = currentData?.reduce((sum, inf) => sum + (inf.rewardAmount || 0), 0) || 0;

  return (
    <div className="marketer-cps-settlement-modal mbm-overlay">
      <div className="marketer-cps-settlement-modal__content mbm" onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div className="marketer-cps-settlement-modal__header mbm-head">
          <div className="marketer-cps-settlement-modal__header-info">
            <h2 className="mbm-title">CPS 결산 상태</h2>
            <div className="marketer-cps-settlement-modal__header-subtitle">
              프로젝트: {projectNumber}
            </div>
          </div>
          <button onClick={onClose} className="marketer-cps-settlement-modal__close-icon mbm-close">✕</button>
        </div>

        {/* 탭 */}
        <div className="marketer-cps-settlement-modal__tabs">
          <button
            onClick={() => setSelectedView('total')}
            className={selectedView === 'total' ? 'marketer-cps-settlement-modal__tab--active' : 'marketer-cps-settlement-modal__tab'}
          >
            총합
          </button>
          {data?.rounds?.map(round => (
            <button
              key={round.roundNumber}
              onClick={() => setSelectedView(String(round.roundNumber))}
              className={selectedView === String(round.roundNumber) ? 'marketer-cps-settlement-modal__tab--active' : 'marketer-cps-settlement-modal__tab'}
            >
              {round.roundNumber}회차
            </button>
          ))}
        </div>

        {/* 결산 미완료 경고 */}
        {currentRound && !currentRound.isCompleted && (
          <div className="marketer-cps-settlement-modal__warning">
            ⚠️ {currentRound.roundNumber}회차의 결산이 완료 처리되지 않았습니다.
          </div>
        )}

        {/* 자동예약 처리 정보 표시 */}
        {currentRound && currentRound.isCompleted && currentRound.isAutoSettled && (
          <div className={`marketer-cps-settlement-modal__auto-settlement-info ${
            currentRound.reservationStatus === 'rejected' 
              ? 'marketer-cps-settlement-modal__auto-settlement-info--rejected' 
              : 'marketer-cps-settlement-modal__auto-settlement-info--approved'
          }`}>
            {currentRound.reservationStatus === 'rejected' ? (
              <>
                <span className="marketer-cps-settlement-modal__auto-settlement-icon">❌</span>
                <span>미결산으로 관리자 거절 건 (고객 유입 없음)</span>
              </>
            ) : (
              <>
                <span className="marketer-cps-settlement-modal__auto-settlement-icon">✅</span>
                <span>미결산으로 관리자 승인 건 (최대 고객 수로 지급)</span>
              </>
            )}
          </div>
        )}

        {/* 테이블 */}
        <div className="marketer-cps-settlement-modal__table-container">
          {loading ? (
            <div className="marketer-cps-settlement-modal__loading">로딩 중...</div>
          ) : !currentData || currentData.length === 0 ? (
            <div className="marketer-cps-settlement-modal__empty">
              데이터가 없습니다.
            </div>
          ) : (
            <table className="marketer-cps-settlement-modal__table">
              <thead>
                <tr>
                  <th>NO</th>
                  <th>인플루언서</th>
                  <th>유입 고객 수</th>
                  <th>지급 포인트</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((inf, index) => (
                  <tr key={inf.influencerId || index}>
                    <td>{index + 1}</td>
                    <td>
                      <div className="marketer-cps-settlement-modal__influencer-name">{inf.influencerName}</div>
                      <div className="marketer-cps-settlement-modal__influencer-id">
                        ({inf.influencerUserId})
                      </div>
                    </td>
                    <td>{inf.customerCount}명</td>
                    <td>{inf.rewardAmount.toLocaleString()}P</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 총합 */}
        <div className="marketer-cps-settlement-modal__summary">
          <span>총 유입 고객: <strong>{totalCustomers}명</strong></span>
          <span>총 지급 포인트: <strong>{totalReward.toLocaleString()}P</strong></span>
        </div>

        {/* 총합 설명 (자동예약 건이 있는 경우만 표시) */}
        {data?.rounds?.some(r => r.isAutoSettled) && (
          <div className="marketer-cps-settlement-modal__note">
            💡 총합에는 승인된 자동예약 건만 포함됩니다. 거절된 건은 제외됩니다.
          </div>
        )}

        {/* 닫기 버튼 */}
        <div className="marketer-cps-settlement-modal__footer mbm-foot">
          <button onClick={onClose} className="mb-btn mb-btn-secondary">닫기</button>
        </div>
      </div>
    </div>
  );
}
